import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./AppBootstrap.vue";
import router from "./router";
import createPortalVuetify from "./plugins/vuetify";
import { installLegacyStoreBridge } from "./plugins/legacyStoreBridge";
import { registerPortalServiceWorker } from "./registerServiceWorker";
import { getRuntimeConfiguration } from "@/helpers/Configuration/ConfigurationFileHelpers";
import { runtimeConfigurationKey } from "@/runtimeConfiguration";
import track from "@/directives/track";
import { isJsonWebTokenCurrent } from "@/services/activity/portalActivitySession";
import { installPortalActivityTracking } from "@/services/activity/portalActivityTracker";
import { onAuthAction } from "@/services/activity/sessionChanges";
import {
  initializePortalHttpClient,
  sendHeldPortalActivityBatch,
  sendPortalActivityBatch,
} from "@/services/httpClient";
import { useAuthStore } from "@/stores/auth";
import { useConfigurationStore } from "@/stores/configuration";
import { useParametersStore } from "@/stores/parameters";
// Side-effect import: applies the dayjs plugin extensions (relativeTime,
// minMax, isSameOrAfter) at startup so no module depends on import ordering.
import "@/helpers/Dates/DateHelpers";
import "@/assets/css/main.css";

async function bootstrap() {
  const runtimeConfiguration = await getRuntimeConfiguration();
  const app = createApp(App);
  const pinia = createPinia();

  app.use(pinia);
  installLegacyStoreBridge(app);

  const configurationStore = useConfigurationStore();
  configurationStore.setConfigurationContents(runtimeConfiguration);

  initializePortalHttpClient(runtimeConfiguration);

  const authStore = useAuthStore();
  authStore.restoreSession();

  const parametersStore = useParametersStore();
  installPortalActivityTracking({
    isEnabled: () => parametersStore.parameterSettings.activityTracking === true,
    getIdentity: () => ({
      isAuthenticated: authStore.isAuthenticated,
      customerId: authStore.selectedAccount?.id ?? "",
      entityId: authStore.selectedAccount?.entityId ?? "",
      awaitingAccount: authStore.isAuthenticated && !authStore.selectedAccount,
    }),
    canSendSignedIn: () => isJsonWebTokenCurrent(authStore.tokens?.jsonWebToken),
    sendHeld: sendHeldPortalActivityBatch,
    send: sendPortalActivityBatch,
  });

  authStore.$onAction((context) =>
    onAuthAction(context, () => authStore.webLogin?.webLoginId ?? "")
  );

  app.directive("track", track);
  app.use(router);
  app.use(createPortalVuetify(runtimeConfiguration.portalThemeVariables));
  app.provide(runtimeConfigurationKey, runtimeConfiguration);

  registerPortalServiceWorker();

  app.mount("#app");
}

void bootstrap();
